import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, KeyRound, Mail } from 'lucide-react';
import TextInput from '../../components/ui/TextInput';
import MfaSetupPanel from '../../components/mfa/MfaSetupPanel';
import RecoveryCodesPanel from '../../components/mfa/RecoveryCodesPanel';
import {
  confirmMfaEnrollment,
  resendMfaEnrollmentCode,
  verifyMfaChallenge,
  verifyMfaEnrollmentEmail,
} from '../../services/authService';
import { FormTitle, FormSubtitle, Form, GradientButton, BackButton, RememberRow, RememberLabel } from './styles';

// Giriş ekranının iki adımlı doğrulama adımları. Sunucu şifreyi doğruladıktan sonra 403 ile
// sınama döner (auth.mfaRequired = kod iste / auth.mfaEnrollmentRequired = kurulum yaptır);
// bu bileşen sınamayı tamamlar ve oturum yanıtını `onComplete`e verir.
//
// Kurulum sırası (araştırma kararı): e-posta kodu → uygulamaya ekle (QR) → ilk kod → kurtarma
// kodları. E-posta kodu olmadan QR gösterilmez: şifreyi ele geçiren biri gerçek kullanıcıdan
// önce kendi telefonunu kaydedemesin.

const RESEND_COOLDOWN_SECONDS = 60;

const Lead = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.secondary.main};
`;

const TextLink = styled.button`
  align-self: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: rgba(255, 255, 255, 0.6);

  &:hover:not(:disabled) { color: #0291EF; }
  &:disabled { cursor: default; opacity: 0.6; }
`;

const Links = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 14px;
`;

const Notice = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
`;

const codeInputProps = {
  inputMode: 'numeric',
  autoComplete: 'one-time-code',
  maxLength: 6,
  pattern: '[0-9]*',
};

export default function MfaLoginStep({ challenge, credentials, onComplete, onBack }) {
  const { t } = useTranslation();
  const enroll = challenge.mode === 'enroll';

  // verify: 'code' | 'recoveryNotice'; enroll: 'email' | 'app' | 'codes'
  const [step, setStep] = useState(enroll ? 'email' : 'code');
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [trustBrowser, setTrustBrowser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setup, setSetup] = useState(null);
  const [session, setSession] = useState(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState(challenge.maskedEmail);
  const [cooldown, setCooldown] = useState(enroll && challenge.emailCodeSent ? RESEND_COOLDOWN_SECONDS : 0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const run = async (fn) => {
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      // Hata toast'u interceptor'da. Sınama bittiyse (deneme hakkı / süre doldu, kilit) bu
      // ekranda kalmanın anlamı yok: girişe dön — yeni giriş yeni sınama açar.
      const key = err?.response?.data?.message;
      if (key === 'mfa.challengeExpired' || key === 'mfa.lockedOut') onBack();
    } finally {
      setLoading(false);
    }
  };

  const trustAllowed = Boolean(challenge.trustBrowserAllowed);
  const trustRow = trustAllowed && (
    <RememberRow>
      <RememberLabel>
        <input type="checkbox" checked={trustBrowser} onChange={(e) => setTrustBrowser(e.target.checked)} />
        {t('mfa.login.trustBrowser', { count: challenge.trustBrowserDays || 30 })}
      </RememberLabel>
    </RememberRow>
  );

  // ── Doğrulama ─────────────────────────────────────────────────────────────

  const submitVerify = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await verifyMfaChallenge({
        challengeToken: challenge.challengeToken,
        code: code.trim(),
        trustBrowser: trustAllowed && trustBrowser,
      });
      const data = res.data?.data;
      if (!data) return;
      if (typeof data.mfaRecoveryCodesRemaining === 'number') {
        setSession(data);
        setStep('recoveryNotice');
        return;
      }
      onComplete(data);
    });
  };

  if (step === 'code') {
    return (
      <>
        <Lead><ShieldCheck size={36} /></Lead>
        <FormTitle>{t('mfa.login.verifyTitle')}</FormTitle>
        <FormSubtitle>{useRecovery ? t('mfa.login.recoverySubtitle') : t('mfa.login.verifySubtitle')}</FormSubtitle>
        <Form onSubmit={submitVerify}>
          {useRecovery ? (
            <TextInput
              key="recovery"
              label={t('mfa.login.recoveryCode')}
              name="recoveryCode"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              autoComplete="off"
              autoFocus
              required
            />
          ) : (
            <TextInput
              key="totp"
              label={t('mfa.login.code')}
              name="mfaCode"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
              required
              {...codeInputProps}
            />
          )}
          {trustRow}
          <GradientButton type="submit" disabled={loading || code.trim().length < 6}>
            <KeyRound size={18} />
            {loading ? t('common.loading') : t('mfa.login.verifySubmit')}
          </GradientButton>
        </Form>
        <Links>
          <TextLink type="button" onClick={() => { setUseRecovery((v) => !v); setCode(''); }}>
            {useRecovery ? t('mfa.login.useApp') : t('mfa.login.useRecovery')}
          </TextLink>
        </Links>
        <BackButton type="button" onClick={onBack}>{t('login.expired.back')}</BackButton>
      </>
    );
  }

  if (step === 'recoveryNotice') {
    return (
      <>
        <Lead><ShieldCheck size={36} /></Lead>
        <FormTitle>{t('mfa.login.recoveryUsedTitle')}</FormTitle>
        <Form onSubmit={(e) => { e.preventDefault(); onComplete(session); }}>
          <Notice>{t('mfa.login.recoveryUsedMessage', { count: session.mfaRecoveryCodesRemaining })}</Notice>
          <GradientButton type="submit">{t('mfa.login.continue')}</GradientButton>
        </Form>
      </>
    );
  }

  // ── Kurulum ───────────────────────────────────────────────────────────────

  const resend = () => run(async () => {
    const res = await resendMfaEnrollmentCode(challenge.challengeToken);
    if (res.data?.status) {
      setMaskedEmail(res.data.data?.maskedEmail || maskedEmail);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    }
  });

  const submitEmail = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await verifyMfaEnrollmentEmail({ challengeToken: challenge.challengeToken, code: code.trim() });
      const data = res.data?.data;
      if (!data) return;
      setSetup(data);
      setCode('');
      setStep('app');
    });
  };

  const submitConfirm = (e) => {
    e.preventDefault();
    run(async () => {
      const res = await confirmMfaEnrollment({
        challengeToken: challenge.challengeToken,
        code: code.trim(),
        trustBrowser: trustAllowed && trustBrowser,
      });
      const data = res.data?.data;
      if (!data) return;
      setSession(data);
      setStep('codes');
    });
  };

  if (step === 'email') {
    return (
      <>
        <Lead><Mail size={36} /></Lead>
        <FormTitle>{t('mfa.login.enrollTitle')}</FormTitle>
        <FormSubtitle>
          {challenge.emailCodeSent || cooldown > 0
            ? t('mfa.login.enrollEmailSubtitle', { email: maskedEmail })
            : t('mfa.login.enrollEmailNotSent', { email: maskedEmail })}
        </FormSubtitle>
        <Form onSubmit={submitEmail}>
          <TextInput
            label={t('mfa.login.emailCode')}
            name="emailCode"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            required
            {...codeInputProps}
          />
          <GradientButton type="submit" disabled={loading || code.length < 6}>
            {loading ? t('common.loading') : t('mfa.login.next')}
          </GradientButton>
        </Form>
        <Links>
          <TextLink type="button" onClick={resend} disabled={loading || cooldown > 0}>
            {cooldown > 0 ? t('mfa.login.resendIn', { seconds: cooldown }) : t('mfa.login.resend')}
          </TextLink>
        </Links>
        <BackButton type="button" onClick={onBack}>{t('login.expired.back')}</BackButton>
      </>
    );
  }

  if (step === 'app') {
    return (
      <>
        <FormTitle>{t('mfa.login.appTitle')}</FormTitle>
        <FormSubtitle>{t('mfa.login.appSubtitle')}</FormSubtitle>
        <MfaSetupPanel setup={setup} tone="dark" />
        <Form onSubmit={submitConfirm} style={{ marginTop: 18 }}>
          <TextInput
            label={t('mfa.login.code')}
            name="mfaCode"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            {...codeInputProps}
          />
          {trustRow}
          <GradientButton type="submit" disabled={loading || code.length < 6}>
            <ShieldCheck size={18} />
            {loading ? t('common.loading') : t('mfa.login.enrollSubmit')}
          </GradientButton>
        </Form>
        <BackButton type="button" onClick={onBack}>{t('login.expired.back')}</BackButton>
      </>
    );
  }

  // step === 'codes' — oturum sunucuda açıldı; kullanıcı kodları kaydettiğini onaylayınca girer.
  return (
    <>
      <Lead><ShieldCheck size={36} /></Lead>
      <FormTitle>{t('mfa.recovery.title')}</FormTitle>
      <RecoveryCodesPanel codes={session?.mfaRecoveryCodes || []} tone="dark" accountLabel={credentials?.email} />
      <Form onSubmit={(e) => { e.preventDefault(); onComplete(session); }} style={{ marginTop: 18 }}>
        <RememberRow>
          <RememberLabel>
            <input type="checkbox" checked={savedCodes} onChange={(e) => setSavedCodes(e.target.checked)} />
            {t('mfa.recovery.saved')}
          </RememberLabel>
        </RememberRow>
        <GradientButton type="submit" disabled={!savedCodes}>{t('mfa.login.continue')}</GradientButton>
      </Form>
    </>
  );
}
